import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import PageHeader from '@/components/feature/PageHeader';
import Button from '@/components/base/Button';
import Switch from '@/components/base/Switch';
import { useAppData, type ServiceKey } from '@/store/AppDataContext';
import type { LaundryItem, ServicePrice } from '@/mocks/items';

const serviceHeaders: Array<{ key: ServiceKey; label: string }> = [
  { key: 'washOnly', label: 'غسيل فقط' },
  { key: 'ironOnly', label: 'كوي فقط' },
  { key: 'washIron', label: 'غسيل وكوي' },
];

function blankItem(): Omit<LaundryItem, 'id'> {
  return {
    name: '',
    image: '',
    active: true,
    washOnly: { normal: 0, urgent: 0, enabled: true },
    ironOnly: { normal: 0, urgent: 0, enabled: true },
    washIron: { normal: 0, urgent: 0, enabled: true },
  };
}

export default function ItemFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { items, addItem, updateItem } = useAppData();

  const existing = id ? items.find((item) => item.id === id) : undefined;
  const isEdit = Boolean(existing);

  const [form, setForm] = useState<Omit<LaundryItem, 'id'>>(
    existing ? { ...existing } : blankItem()
  );
  const [nameError, setNameError] = useState('');

  const updateService = (key: ServiceKey, patch: Partial<ServicePrice>) => {
    setForm((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const submit = () => {
    if (!form.name.trim()) {
      setNameError('اسم القطعة مطلوب');
      return;
    }
    if (isEdit && existing) {
      updateItem(existing.id, form);
      navigate('/items', { state: { toast: { type: 'success', message: 'تم تحديث القطعة بنجاح' } } });
    } else {
      addItem(form);
      navigate('/items', { state: { toast: { type: 'success', message: 'تمت إضافة القطعة بنجاح' } } });
    }
  };

  return (
    <DashboardLayout title={isEdit ? 'تعديل القطعة' : 'إضافة قطعة جديدة'}>
      <div className="mx-auto w-full max-w-[760px]">
        <PageHeader
          title={isEdit ? `تعديل: ${existing?.name}` : 'إضافة قطعة جديدة'}
          subtitle="حدّد اسم القطعة وأسعار الخدمات الستة."
          backTo="/items"
        />

        <div className="rounded-card border border-background-200 bg-white p-5 shadow-card">
          <div className="space-y-5">
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-foreground-800">
                اسم القطعة <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(event) => {
                  setForm((prev) => ({ ...prev, name: event.target.value }));
                  if (event.target.value.trim()) setNameError('');
                }}
                placeholder="مثال: قميص رجالي"
                className={[
                  'h-12 w-full rounded-field border px-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2',
                  nameError
                    ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-100'
                    : 'border-background-300 focus:border-secondary-400 focus:ring-secondary-100',
                ].join(' ')}
              />
              {nameError && <p className="mt-1.5 text-[12px] text-danger-600">{nameError}</p>}
            </div>

            <div>
              <p className="mb-1.5 text-[13px] font-medium text-foreground-800">
                صورة توضيحية (اختياري)
              </p>
              <div className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-card border border-dashed border-background-300 bg-background-50 px-4 py-6 text-center transition-colors hover:border-secondary-300 hover:bg-background-100">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-image-add-line text-xl" aria-hidden="true"></i>
                </span>
                <span className="text-[13px] font-medium text-foreground-700">
                  اسحب الصورة هنا أو اضغط للاختيار
                </span>
                <span className="text-[12px] text-foreground-400">
                  PNG أو JPG — بحد أقصى 5 ميجابايت
                </span>
              </div>
            </div>

            <div>
              <h4 className="mb-3 font-semibold text-[14px] text-foreground-950">الأسعار الستة</h4>
              <div className="overflow-hidden rounded-lg border border-background-200">
                <table className="w-full border-collapse text-right">
                  <thead>
                    <tr className="bg-background-50">
                      <th className="border border-background-100 px-3 py-2 text-right text-[11px] font-semibold text-foreground-500">
                        الأولوية
                      </th>
                      {serviceHeaders.map(({ key, label }) => (
                        <th
                          key={key}
                          className="border border-background-100 px-3 py-2 text-center text-[11px] font-semibold text-foreground-500"
                        >
                          <div className="flex items-center justify-center gap-1.5">
                            <Switch
                              checked={(form[key] as ServicePrice).enabled}
                              onChange={(checked) => updateService(key, { enabled: checked })}
                              size="sm"
                            />
                            {label}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(['normal', 'urgent'] as const).map((tier) => (
                      <tr key={tier}>
                        <td className="border border-background-100 px-3 py-2 text-[12px] font-medium text-foreground-600">
                          {tier === 'normal' ? 'عادي' : 'مستعجل'}
                        </td>
                        {serviceHeaders.map(({ key }) => {
                          const service = form[key] as ServicePrice;
                          return (
                            <td
                              key={key}
                              className="border border-background-100 px-2 py-1.5 text-center"
                            >
                              {service.enabled ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    value={service[tier] || ''}
                                    onChange={(event) =>
                                      updateService(key, { [tier]: Number(event.target.value) })
                                    }
                                    placeholder="0"
                                    className="w-full rounded border border-background-300 px-2 py-1 text-center text-[13px] font-semibold text-foreground-950 focus:border-secondary-400 focus:outline-none"
                                  />
                                  <span className="shrink-0 text-[11px] text-foreground-400">ر.س</span>
                                </div>
                              ) : (
                                <span className="text-[13px] text-foreground-300 line-through">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 border-t border-background-100 pt-5 sm:flex-row">
            <Button variant="outline" size="lg" className="flex-1" onClick={() => navigate('/items')}>
              إلغاء
            </Button>
            <Button variant="cta" size="lg" className="flex-1" icon="ri-save-line" onClick={submit}>
              حفظ
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}